import React from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  Grid,
  Paper,
  Slider,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Psychology as PsychologyIcon,
  Token as TokenIcon,
  AccessTime as AccessTimeIcon,
  Cached as CachedIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import SettingsRowShell from './SettingsRowShell';

const SmartSearchRow = ({
  smartSearchSettings,
  loadingSmartSearch,
  savingSmartSearch,
  minSimilarity,
  setMinSimilarity,
  minConfidence,
  setMinConfidence,
  cacheTTL,
  setCacheTTL,
  maxResults,
  setMaxResults,
  onSave,
  onRefresh,
}) => {
  const modelName = smartSearchSettings?.llm_settings?.model_name || 'N/A';
  const canEdit = smartSearchSettings?.can_edit;

  return (
    <SettingsRowShell
      icon={<SmartToyIcon />}
      title="Smart Search Settings"
      description={canEdit ? 'LLM configuration and token usage' : 'LLM configuration and token usage (read-only)'}
      status={{ label: modelName, color: 'info' }}
      loading={loadingSmartSearch}
      actions={
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {canEdit && (
            <Button
              variant="contained"
              size="small"
              startIcon={savingSmartSearch ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={onSave}
              disabled={savingSmartSearch || loadingSmartSearch}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              {savingSmartSearch ? 'Saving…' : 'Save'}
            </Button>
          )}
          <IconButton onClick={onRefresh} disabled={loadingSmartSearch} size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      }
    >
      {loadingSmartSearch ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : smartSearchSettings ? (
        <Grid container spacing={3}>
          {/* LLM Configuration */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PsychologyIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold">LLM Configuration</Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Row label="Model"><Chip label={modelName} size="small" color="primary" /></Row>
                <Row label="Temperature">{smartSearchSettings.llm_settings?.temperature}</Row>
                <Row label="Max Output Tokens">{smartSearchSettings.llm_settings?.max_output_tokens}</Row>

                {canEdit ? (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="caption" color="primary" fontWeight="bold">Configurable</Typography>

                    <SliderField
                      label="Min Similarity"
                      value={minSimilarity}
                      onChange={setMinSimilarity}
                      min={10} max={90} step={5}
                      marks={[{ value: 10, label: '10%' }, { value: 50, label: '50%' }, { value: 90, label: '90%' }]}
                      format={(v) => `${v}%`}
                      hint="Minimum semantic similarity score for search results"
                    />
                    <SliderField
                      label="Min Confidence"
                      value={minConfidence}
                      onChange={setMinConfidence}
                      min={10} max={90} step={5}
                      marks={[{ value: 10, label: '10%' }, { value: 50, label: '50%' }, { value: 90, label: '90%' }]}
                      format={(v) => `${v}%`}
                      hint="Minimum LLM confidence to proceed with navigation"
                    />
                    <SliderField
                      label="Cache TTL"
                      value={cacheTTL}
                      onChange={setCacheTTL}
                      min={30} max={3600} step={30}
                      marks={[{ value: 60, label: '1m' }, { value: 300, label: '5m' }, { value: 900, label: '15m' }, { value: 1800, label: '30m' }, { value: 3600, label: '1h' }]}
                      format={(v) => (v >= 60 ? `${Math.floor(v / 60)}m ${v % 60}s` : `${v}s`)}
                      chipFormat={(v) => `${v}s`}
                      chipColor="info"
                      hint="How long to cache LLM responses (30s – 1h)"
                    />
                    <SliderField
                      label="Max Results"
                      value={maxResults}
                      onChange={setMaxResults}
                      min={10} max={200} step={10}
                      marks={[{ value: 10, label: '10' }, { value: 50, label: '50' }, { value: 100, label: '100' }, { value: 200, label: '200' }]}
                      format={(v) => `${v}`}
                      chipColor="info"
                      hint="Maximum number of results to return from search"
                    />
                  </>
                ) : (
                  <>
                    <Row label="Cache TTL">{smartSearchSettings.llm_settings?.cache_ttl_seconds}s</Row>
                    <Row label="Min Similarity">
                      {((smartSearchSettings.llm_settings?.min_similarity_threshold || 0.5) * 100).toFixed(0)}%
                    </Row>
                    <Row label="Confidence">
                      {((smartSearchSettings.llm_settings?.min_confidence_threshold || 0.5) * 100).toFixed(0)}%
                    </Row>
                  </>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Token Usage */}
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TokenIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold">Token Usage Statistics</Typography>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Queries</TableCell>
                      <TableCell align="right">Input</TableCell>
                      <TableCell align="right">Output</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Cache</TableCell>
                      <TableCell align="right">Avg</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {['usage_today', 'usage_week', 'usage_month'].map((period) => {
                      const u = smartSearchSettings[period];
                      if (!u) return null;
                      const lbl = period === 'usage_today' ? 'Today' : period === 'usage_week' ? 'This Week' : 'This Month';
                      return (
                        <TableRow key={period} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTimeIcon fontSize="small" color="action" />{lbl}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={u.total_queries.toLocaleString()} size="small" color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{u.total_input_tokens.toLocaleString()}</TableCell>
                          <TableCell align="right">{u.total_output_tokens.toLocaleString()}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">{u.total_tokens.toLocaleString()}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              icon={<CachedIcon fontSize="small" />}
                              label={`${u.cache_hit_rate.toFixed(1)}%`}
                              size="small"
                              color={u.cache_hit_rate > 30 ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">{u.avg_response_time_ms.toFixed(0)}ms</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Summary boxes */}
              <Grid container spacing={1.5} sx={{ mt: 2 }}>
                {[
                  { label: 'Queries Today', value: smartSearchSettings.usage_today?.total_queries || 0, bg: 'primary.50', fg: 'primary.main' },
                  { label: 'Tokens Today', value: `${((smartSearchSettings.usage_today?.total_tokens || 0) / 1000).toFixed(1)}K`, bg: 'success.50', fg: 'success.main' },
                  { label: 'Tokens This Month', value: `${((smartSearchSettings.usage_month?.total_tokens || 0) / 1000).toFixed(1)}K`, bg: 'info.50', fg: 'info.main' },
                  { label: 'Avg Tokens/Query', value: smartSearchSettings.usage_today?.avg_tokens_per_query?.toFixed(0) || 0, bg: 'warning.50', fg: 'warning.main' },
                ].map((s) => (
                  <Grid item xs={6} md={3} key={s.label}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: s.bg, borderRadius: 1 }}>
                      <Typography variant="h6" color={s.fg} fontWeight="bold">{s.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info">Smart search settings could not be loaded. Click refresh to try again.</Alert>
      )}
    </SettingsRowShell>
  );
};

/* ------------------------------------------------------------------ */
/* Tiny helpers (local to this file)                                   */
/* ------------------------------------------------------------------ */
const Row = ({ label, children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight="medium">{children}</Typography>
  </Box>
);

const SliderField = ({ label, value, onChange, min, max, step, marks, format, chipFormat, chipColor = 'secondary', hint }) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Chip label={chipFormat ? chipFormat(value) : format(value)} size="small" color={chipColor} />
    </Box>
    <Slider
      value={value}
      onChange={(_, v) => onChange(v)}
      min={min}
      max={max}
      step={step}
      marks={marks}
      valueLabelDisplay="auto"
      valueLabelFormat={format}
      size="small"
    />
    {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
  </Box>
);

export default SmartSearchRow;
